import React, { useState } from "react";
import PropTypes from "prop-types";
import formatDate from "date-fns/format";
import { Button, Card, CardBody } from "reactstrap";
import Loading from "common/components/loading";
import DatePicker from "common/components/date-picker";
import TabbedNavigation from "common/components/tabbed-navigation";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import { useGetCampaignPerformanceQuery } from "../campaigns-api-endpoints";
import PerformanceStatTile from "./performance-stat-tile";
import PerformanceChart from "./performance-chart";
import {
  STAT_TYPE_ATTRS,
  STAT_TYPE_LABELS,
  STAT_TYPE_QUESTIONS,
  CTV_STAT_TYPE_QUESTIONS
} from "./performance-stat-types";

const propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  clientToken: PropTypes.string,
  campaignType: PropTypes.string
};

const PerformanceSection = ({ accountId, campaignId, clientToken = null, campaignType = null }) => {
  const [statType, setStatType] = useState("views");
  const [cumulativeViewMode, setCumulativeViewMode] = useState(true);
  const [dateRange, setDateRange] = useState({ fromDate: undefined, toDate: undefined });
  const toQueryDate = date => formatDate(date, "yyyy-MM-dd");
  const attr = STAT_TYPE_ATTRS[statType];

  const { data } = useGetCampaignPerformanceQuery({
    accountId,
    campaignId,
    clientToken,
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate
  });

  if (!data) {
    return (
      <div className="performance-section card" id="performance-section">
        <Loading loading title="Loading performance" />
      </div>
    );
  }

  const trackPerformanceClick = action =>
    mixpanelService.track("Campaign.PerformanceClick", {
      ...pageData.get("mixpanelData"),
      action,
      campaign_id: campaignId,
      campaign_type: campaignType
    });

  const handleApplyDateRange = (from, to) => {
    if (!from || !to) return;
    trackPerformanceClick("date_change");
    setDateRange({ fromDate: toQueryDate(from), toDate: toQueryDate(to) });
  };

  const handleStatTypeChange = type => {
    trackPerformanceClick(`${type}_stats`);
    setStatType(type);
  };

  const toggleCumulativeViewMode = () => {
    trackPerformanceClick(cumulativeViewMode ? "daily_views" : "total_views");
    setCumulativeViewMode(!cumulativeViewMode);
  };

  const tabs = data.availableStatTypes.map(type => ({
    id: type,
    shortLabel: STAT_TYPE_LABELS[type],
    longLabel: STAT_TYPE_LABELS[type]
  }));

  const statTypeQuestions = data.isCtv ? CTV_STAT_TYPE_QUESTIONS : STAT_TYPE_QUESTIONS;

  return (
    <Card className="performance-section" id="performance-section">
      <CardBody>
        <div className="performance-section__header">
          <h2 className="performance-section__title">{statTypeQuestions[statType]}</h2>
          <TabbedNavigation
            tabs={tabs}
            activeTabId={statType}
            onTabChange={handleStatTypeChange}
            testIdPrefix="performance-stat-type"
            className="tabbed-navigation-grouped"
          />
        </div>
        <div className="performance-section__overview">
          <PerformanceStatTile
            label="Selected dates"
            value={data.selectedDateStats[attr]}
            attr={attr}
            products={data.products}
            breakdown={data.selectedDateStats.products}
          />
          <PerformanceStatTile
            label="All-Time"
            value={data.lifetimeStats[attr]}
            attr={attr}
            products={data.products}
            breakdown={data.lifetimeStats.products}
          />
        </div>
        <div className="performance-section__date-range">
          <DatePicker
            isRange
            placement="bottom-start"
            format="MMM d, yyyy"
            className="form-control"
            label="Select date range"
            from={data.fromDate}
            to={data.toDate}
            minDate={data.minDate}
            maxDate={data.maxDate}
            onChange={handleApplyDateRange}
          />
        </div>
        <PerformanceChart
          series={cumulativeViewMode ? data.cumulativeSeries : data.dailySeries}
          products={data.products}
          statType={statType}
          cumulativeViewMode={cumulativeViewMode}
          trackPerformanceClick={() => trackPerformanceClick("toggle_legend_series")}
        />
        <div className="performance-section__view-mode">
          <Button color="link" className="p-0" onClick={toggleCumulativeViewMode}>
            {cumulativeViewMode
              ? `View Daily ${STAT_TYPE_LABELS[statType]}`
              : `View Total ${STAT_TYPE_LABELS[statType]}`}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

PerformanceSection.propTypes = propTypes;

export default PerformanceSection;
